import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  Dropdown,
  Menu,
  Modal,
  Radio,
  Row,
  Skeleton,
  Space,
  Tag,
  Table,
  message,
} from "antd";
import {
  EditOutlined,
  MoreOutlined,
  PlusOutlined,
  ReloadOutlined,
  StarOutlined,
} from "@ant-design/icons";
import { FormInstance } from "antd/lib/form";
import TicketLayoutEditor from "./TicketLayoutEditor";
import { helper } from "@src/controls/controlHelper";
import dayjs from "dayjs";

interface LayoutItem {
  id: number;
  name?: string;
  default?: boolean;
  background?: string;
  layout?: any;
  context?: Record<string, unknown>;
  pageWidth?: number;
  pageHeight?: number;
}

interface LayoutSectionProps {
  form: FormInstance;
  pageId?: number;
  query?: any;
}

const LayoutSection: React.FC<LayoutSectionProps> = ({
  form,
  pageId,
  query,
}) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<LayoutItem[]>([]);
  const [openEditor, setOpenEditor] = useState(false);
  const [editing, setEditing] = useState<LayoutItem | null>(null);
  const [savingDefaultId, setSavingDefaultId] = useState<number | null>(null);
  const [selectedId, setSelectedId] = useState<number | undefined>(() =>
    form
      ? (form.getFieldValue(["envent", "ticketLayoutId"]) as number | undefined)
      : undefined,
  );

  // eventIdForFilter no longer needed; backend endpoint supports default lookup

  const selectLayout = useCallback(
    (layout: LayoutItem) => {
      if (selectedId === layout.id) return;

      form.setFieldsValue({ envent: { ticketLayoutId: layout.id } });
      setSelectedId(layout.id);
      message.success(`Đã chọn ${layout.name || `Layout #${layout.id}`}`);
    },
    [form, selectedId],
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const pageInfo = await helper.getPage(pageId || 120);
      const rs: any = await helper.callPageApi(
        pageInfo,
        "find_ticketLayout",
        {},
      );
      const raw = rs?.data?.data ?? rs?.data ?? rs ?? {};
      let list: LayoutItem[] = [];
      if (Array.isArray(raw)) list = raw as any;
      else if (Array.isArray(raw?.data)) list = raw.data as any;
      else if (Array.isArray(raw?.list)) list = raw.list as any;
      setItems(list);
      // Auto-select default layout if ticketLayoutId is not set
      const current = form.getFieldValue(["envent", "ticketLayoutId"]);
      if (current == null || current === undefined || current === "") {
        const defaultLayout = list.find((item) => Boolean(item.default));
        if (defaultLayout?.id) {
          form.setFieldsValue({
            envent: { ticketLayoutId: defaultLayout.id },
          });
          setSelectedId(defaultLayout.id);
        }
        return;
      }

      setSelectedId(Number(current));
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [pageId, form]);

  const setDefaultLayout = useCallback(
    async (layout: LayoutItem) => {
      try {
        setSavingDefaultId(layout.id);
        const pageInfo = await helper.getPage(pageId || 120);
        await helper.callPageApi(pageInfo, "update_layout_ticket", {
          id: layout.id,
          default: true,
        });
        message.success("Đã cài đặt mặc định");
        form.setFieldsValue({ envent: { ticketLayoutId: layout.id } });
        setSelectedId(layout.id);
        load();
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Cài đặt mặc định thất bại";
        message.error(errorMessage);
      } finally {
        setSavingDefaultId(null);
      }
    },
    [form, load, pageId],
  );

  useEffect(() => {
    load();
  }, [load]);

  const columns = [
    {
      key: "selection",
      width: 48,
      render: (_: unknown, record: LayoutItem) => (
        <Radio
          checked={selectedId === record.id}
          aria-label={`Chọn ${record.name || `Layout #${record.id}`}`}
          onClick={(event) => event.stopPropagation()}
          onChange={() => selectLayout(record)}
        />
      ),
    },
    {
      title: "#",
      key: "index",
      width: 48,
      render: (_: unknown, __: LayoutItem, index: number) => index + 1,
    },
    {
      title: "Tên mẫu vé",
      dataIndex: "name",
      render: (_: unknown, record: LayoutItem) => (
        <Space size={8}>
          <strong>{record.name || `Layout #${record.id}`}</strong>
          {record.default && <Tag icon={<StarOutlined />}>Mặc định</Tag>}
        </Space>
      ),
    },
    {
      title: "Ngày cập nhật",
      dataIndex: "updatedAt",
      width: 220,
      render: (_: unknown, record: Record<string, unknown>) => {
        const updatedAt =
          record.updatedAt ||
          record.updated_at ||
          record.updated ||
          record.modifiedAt ||
          record.updatedTime;
        const updatedDate = dayjs(updatedAt as string | number | Date);
        return updatedDate.isValid()
          ? updatedDate.format("DD/MM/YYYY HH:mm:ss")
          : "-";
      },
    },
    {
      title: "Thao tác",
      key: "actions",
      width: 180,
      render: (_: unknown, record: LayoutItem) => {
        const actionMenu = (
          <Menu>
            {!record.default && (
              <Menu.Item
                key="set-default"
                icon={<StarOutlined />}
                disabled={savingDefaultId === record.id}
                onClick={() => setDefaultLayout(record)}
              >
                Đặt làm mặc định
              </Menu.Item>
            )}
          </Menu>
        );

        return (
          <div onClick={(event) => event.stopPropagation()}>
            <Space size={8}>
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => {
                  setEditing(record);
                  setOpenEditor(true);
                }}
              >
                Chỉnh sửa
              </Button>
              {!record.default && (
                <Dropdown overlay={actionMenu} trigger={["click"]}>
                  <Button
                    type="text"
                    icon={<MoreOutlined />}
                    aria-label={`Mở thêm thao tác cho ${
                      record.name || `Layout #${record.id}`
                    }`}
                  />
                </Dropdown>
              )}
            </Space>
          </div>
        );
      },
    },
  ];

  const initialForEditor = useMemo(() => {
    if (!editing) return undefined as any;
    // Hỗ trợ cả payload mảng trang và object bọc mảng trang từ API.
    const layoutContainer = editing.layout as any;
    const pages = Array.isArray(layoutContainer)
      ? layoutContainer
      : Array.isArray(layoutContainer?.layout)
      ? layoutContainer.layout
      : layoutContainer && typeof layoutContainer === "object"
      ? [layoutContainer]
      : [];
    const page = pages[0] || {};
    return {
      id: editing.id,
      name: editing.name,
      default: editing.default,
      background: page?.background || editing.background,
      pageWidth: page?.pageWidth || editing.pageWidth,
      pageHeight: page?.pageHeight || editing.pageHeight,
      layout: page?.layout || [],
      context: page?.context || editing.context || {},
    } as any;
  }, [editing]);

  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={load} disabled={loading}>
              Làm mới
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null);
                setOpenEditor(true);
              }}
            >
              Thiết kế layout mới
            </Button>
          </Space>
        </Col>
      </Row>

      {loading ? (
        <Skeleton active />
      ) : (
        <Table
          rowKey={(record) => String(record.id)}
          dataSource={items}
          columns={columns as any}
          pagination={false}
          scroll={{ x: 900 }}
          onRow={(record) => ({
            onClick: () => selectLayout(record),
            style: { cursor: "pointer" },
          })}
        />
      )}

      <Modal
        visible={openEditor}
        onCancel={() => setOpenEditor(false)}
        footer={null}
        width="80vw"
        bodyStyle={{ padding: 0 }}
        destroyOnClose
        title={editing ? "Chỉnh sửa Layout vé" : "Thiết kế Layout vé"}
      >
        <TicketLayoutEditor
          pageId={pageId}
          eventMeta={
            (Array.isArray(form.getFieldValue(["envent", "metaProperties"]))
              ? form.getFieldValue(["envent", "metaProperties"])
              : []) as any
          }
          ticketMeta={
            (() => {
              const DEFAULTS = ["GATE", "ZONE", "FLOOR", "SEAT"]; // fallback để preview
              const globalMeta = Array.isArray(form.getFieldValue(["metaData"]))
                ? form.getFieldValue(["metaData"])
                : [];
              const tickets = Array.isArray(form.getFieldValue(["ticket"]))
                ? form.getFieldValue(["ticket"])
                : [];
              const mergedMap: Record<string, any> = {};
              const keyOf = (m: any) => String(m?.name || "").toUpperCase();
              // Ưu tiên giá trị từ meta của vé (đặc biệt là vé đầu tiên)
              tickets.forEach((t: any) => {
                const arr = Array.isArray(t?.metaData) ? t.metaData : [];
                arr.forEach((m: any) => {
                  const k = keyOf(m);
                  if (!k) return;
                  const cur = mergedMap[k];
                  if (!cur || !cur.value) mergedMap[k] = m; // override nếu chưa có hoặc giá trị rỗng
                });
              });
              // Điền thêm meta chung cho khóa chưa có
              globalMeta.forEach((m: any) => {
                const k = keyOf(m);
                if (!k) return;
                if (!mergedMap[k]) mergedMap[k] = m;
              });
              // Fallback 4 khóa mặc định nếu vẫn thiếu
              DEFAULTS.forEach((nm) => {
                if (!mergedMap[nm]) mergedMap[nm] = { name: nm, value: "" };
              });
              return Object.values(mergedMap);
            })() as any
          }
          eventValues={(() => {
            const ev = form.getFieldValue(["envent"]) || {};
            const firstTicket = (form.getFieldValue(["ticket"]) || [])[0];
            return {
              ...ev,
              price: firstTicket?.price,
              ticketTypeName: firstTicket?.name,
            };
          })()}
          // @ts-ignore - new props for edit mode
          layoutId={editing?.id}
          // @ts-ignore - new props for initial data
          initialPage={initialForEditor}
          // @ts-ignore - extras for name/default
          initialName={editing?.name}
          initialDefault={editing?.default}
          onClose={() => setOpenEditor(false)}
          onSaved={(data: any) => {
            const id = data?.id ?? data?.data?.id ?? data?.data?.data?.id;
            if (id) {
              const selectedLayoutId = Number(id);
              form.setFieldsValue({
                envent: { ticketLayoutId: selectedLayoutId },
              });
              setSelectedId(selectedLayoutId);
            }
            setOpenEditor(false);
            load();
          }}
        />
      </Modal>
    </>
  );
};

export default LayoutSection;
